import { useLocation } from "react-router";

import { t } from "i18next";

import { StarBorder, StarRate } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";

import { notificationAtom } from "@/src/state/notificationAtom";

import { auctionOverlayIconBoxSx } from "@/src/components/molecules/auctionOverlayIcon.styles";

import { usePostFavourite } from "@/src/queries/dealer/crudFavourite";
import { DealerQueryKeys } from "@/src/queries/dealer/dealerFunctions";
import { AuctionState, DealerAuctionDTO } from "@/src/types/types";

import { useSetAtom } from "jotai";

/**
 * Get queryKey that needs state update depending on path.
 * Only places favourite is toggled are:
 * - HomePage
 * - My Auctions Page
 * - Auction Details Page
 * @param path
 * @returns
 */
const getQueryKeyByLocation = (path: string) => {
  switch (path) {
    case "/dealer":
    case "/dealer/all-active":
      return DealerQueryKeys.AUCTIONS;
    case "/dealer/my":
      return DealerQueryKeys.MY_ACTIVE_AUCTIONS;
    default:
      return DealerQueryKeys.AUCTION;
  }
};
export interface FavouriteProps {
  auction: DealerAuctionDTO;
}

export const Favourite = (props: FavouriteProps) => {
  const { auction } = props;
  const location = useLocation();
  const queryKey = getQueryKeyByLocation(location.pathname);
  const setNotification = useSetAtom(notificationAtom);

  const { mutate: toggleFavourite } = usePostFavourite(queryKey);

  const handleToggleFavourite = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // Show notification immediately for instant feedback (optimistic)
    setNotification({
      text: auction.isFavourite
        ? `${t("dealer.userActions.favourites.deleted", {
            regno: auction.auctionItem.vehicle?.regNo
          })}`
        : `${t("dealer.userActions.favourites.added", {
            regno: auction.auctionItem.vehicle?.regNo
          })}`,
      variant: auction.isFavourite ? "info" : "success"
    });

    toggleFavourite(
      {
        auctionId: auction.id
      },
      {
        onError: (error) => {
          console.error("Failed to toggle favourite status:", error);
          setNotification({
            text: `${t("dealer.userActions.favourites.error")}`,
            variant: "error"
          });
        }
      }
    );
  };

  const isFavourite = Boolean(auction.isFavourite);

  const label = isFavourite
    ? `${t("dealer.userActions.favourites.delete", {
        regno: auction.auctionItem.vehicle?.regNo
      })}`
    : `${t("dealer.userActions.favourites.add", {
        regno: auction.auctionItem.vehicle?.regNo
      })}`;

  return (
    auction.auctionState === AuctionState.STARTED && (
      <Tooltip title={label}>
        <IconButton
          aria-label={label}
          onClick={handleToggleFavourite}
          sx={[
            auctionOverlayIconBoxSx,
            (theme) => ({
              position: "relative",
              "@media (hover: hover)": {
                "&:hover": {
                  backgroundColor: "transparent"
                },
                "&:hover .favourite-icon-outline": {
                  // Not favourited: outline stays visible, just tints red.
                  // Favourited: outline fades in to preview un-favouriting.
                  opacity: 1,
                  ...(!isFavourite && {
                    color: theme.palette.primary.main
                  })
                },
                // Filled star only ever shows for the already-favourited resting
                // state — hover always hides it (previewing un-favourite via the
                // outline above), it never appears just from hovering.
                "&:hover .favourite-icon-filled": {
                  opacity: 0
                }
              }
            })
          ]}
        >
          <StarBorder
            className="favourite-icon-outline"
            sx={{
              position: "absolute",
              width: "30px",
              height: "30px",
              opacity: isFavourite ? 0 : 1,
              transition: "opacity 0.15s ease, color 0.15s ease",
              color: (theme) =>
                isFavourite
                  ? theme.palette.primary.main
                  : theme.palette.common.white
            }}
          />
          <StarRate
            className="favourite-icon-filled"
            sx={{
              position: "absolute",
              width: "30px",
              height: "30px",
              opacity: isFavourite ? 1 : 0,
              transition: "opacity 0.15s ease",
              color: (theme) => theme.palette.primary.main
            }}
          />
        </IconButton>
      </Tooltip>
    )
  );
};
